import { DEFAULT_TIMEZONE } from "./constants";

/* eslint-disable import/no-duplicates */
import {
  addDays,
  differenceInCalendarDays as differenceInCalendarDaysFn,
  differenceInMilliseconds,
  format,
  setHours,
  setMinutes,
  setSeconds,
} from "date-fns";
import { nb } from "date-fns/locale";
import { formatInTimeZone, utcToZonedTime } from "date-fns-tz";

/**
 * Formats a number with Norwegian locale and displays the sign except for zero.
 * Format: +n, -n, 0
 * Example: +123, -123, 0
 */
export const DELAY_NUMBER_FORMAT = new Intl.NumberFormat("nb-NO", {
  signDisplay: "exceptZero",
});

/**
 * Formats a time in short style.
 * Format: HH:mm
 * Example: 13:45
 */
export const SHORT_TIME_FORMAT = new Intl.DateTimeFormat("nb-NO", {
  timeStyle: "short",
});

/**
 * Formats a date with numeric month and day.
 * Format: d.M.
 * Example: 6.7.
 */
export const SHORT_DATE_FORMAT = new Intl.DateTimeFormat("nb-NO", {
  month: "numeric",
  day: "numeric",
});

/**
 * Formats a date with 2-digit month, day, and year.
 * Format: dd.MM.yy
 * Example: 06.07.23
 */
export const MEDIUM_DATE_FORMAT = new Intl.DateTimeFormat("nb-NO", {
  month: "2-digit",
  day: "2-digit",
  year: "2-digit",
});

/**
 * Formats a date with 2-digit day, month, and numeric year.
 * Format: dd.MM.yyyy
 * Example: 06.07.2023
 */
const FULL_DATE_FORMAT = new Intl.DateTimeFormat("nb-NO", {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
});

/**
 * Formats a date and time with 2-digit day, month, year, hour, and minute.
 * Format: dd.MM.yy HH:mm
 * Example: 06.07.23 13:45
 */
export const DATE_TIME_FORMAT = new Intl.DateTimeFormat("nb-NO", {
  month: "2-digit",
  day: "2-digit",
  year: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
});

/**
 * Formats a date with short weekday.
 * Format: EEE
 * Example: tir.
 */
export const SHORT_WEEKDAY_FORMAT = new Intl.DateTimeFormat("nb-NO", {
  weekday: "short",
});

/**
 * Formats a date with long month, numeric day, and long weekday.
 * Format: EEEE d. MMMM
 * Example: tirsdag 6. juni
 */
export const LONG_DATE_FORMAT = new Intl.DateTimeFormat("nb-NO", {
  month: "long",
  day: "numeric",
  weekday: "long",
});

export const LONG_DAY_MONTH_FORMAT = new Intl.DateTimeFormat("nb-NO", {
  month: "long",
  day: "numeric",
});

/**
 * Formats a relative time in Norwegian locale with auto numeric.
 * Format: P
 * Example: i går, om 3 dager
 */
export const RELATIVE_TIME_FORMAT = new Intl.RelativeTimeFormat("nb-NO", {
  numeric: "auto",
});

const RELATIVE_DAY_MAP = {
  TOMORROW: 1,
  TODAY: 0,
  YESTERDAY: -1,
};

export const formatDateWithRelativeTerms = (
  date: Date,
  relativeDays: (keyof typeof RELATIVE_DAY_MAP)[],
  showDate?: boolean,
): string => {
  const differenceInCalendarDays = differenceInCalendarDaysFn(date, new Date());

  const mappedRelativeDays = relativeDays.map((key) => RELATIVE_DAY_MAP[key]);

  if (mappedRelativeDays.includes(differenceInCalendarDays)) {
    if (!showDate) {
      return `${RELATIVE_TIME_FORMAT.format(differenceInCalendarDays, "day")} kl. ${SHORT_TIME_FORMAT.format(date)}`;
    }
    return `${RELATIVE_TIME_FORMAT.format(differenceInCalendarDays, "day")} ${FULL_DATE_FORMAT.format(date)}`;
  }

  return DATE_TIME_FORMAT.format(date);
};

/**
 * Sort entities by some date property, earliest first.
 *
 * @param key - The key to sort by
 * @returns - A comparator function usable in Array.sort or Array.toSorted
 */
export const byDateProperty = <T>(key: keyof T) => {
  return (a: T, b: T) => {
    const aTime = new Date(a[key] as unknown as string).getTime();
    const bTime = new Date(b[key] as unknown as string).getTime();
    if (isNaN(aTime) || isNaN(bTime)) {
      return 0;
    }

    return aTime - bTime;
  };
};

/**
 * Gets the local timezone from the users current browsersession
 * Default is `Europe/Oslo` if the local timezone is not possible to get.
 *
 * @returns The local timezone as a string for the users current browser session
 */
const getLocalTimeZone = (): string =>
  Intl.DateTimeFormat().resolvedOptions().timeZone || DEFAULT_TIMEZONE;

/**
 * Formats a date to a string indicating to the user when the notification has occurred.
 * The formatted date follows the users current sessions timezone in order to show correct time.
 *
 * @param date - The date to format.
 * @returns A formatted string representing the notification received date.
 */
export const formatDateForNotification = (date: Date): string => {
  const timeZone = getLocalTimeZone();

  return formatInTimeZone(date, timeZone, "EEEE dd'.' MMM HH:mm", {
    locale: nb,
  });
};

/**
 * Calculate seconds until a specific time of day in Oslo timezone.
 * Useful for cache TTLs that should expire at a consistent time each day.
 */
type TimeOfDay = {
  hour: number;
  minutes?: number;
};
export const getMilliSecondsUntilTimeOfDay = ({
  hour,
  minutes = 0,
}: TimeOfDay) => {
  const nowInOslo = utcToZonedTime(new Date(), DEFAULT_TIMEZONE);

  let targetTime = setSeconds(
    setMinutes(setHours(nowInOslo, hour), minutes),
    0,
  );

  // If target time has already passed today, move to tomorrow
  if (nowInOslo >= targetTime) {
    targetTime = addDays(targetTime, 1);
  }

  return differenceInMilliseconds(targetTime, nowInOslo);
};

export const formatApiDate = (date: Date) => format(date, "yyyy-MM-dd");
